<template>
	<div class="event-milestones" :data-event-type="attributes.type">
		<div class="event-milestones__total">
			<div class="event-milestones__total-left">
				<span> {{ `${translate("event.total_produced")}: ` }} </span>
				<AppPrice
					:fontSize="14.5"
					:amount="eventiumAccumulated"
					:currency="ResourceType.EVENTIUM"
					:resourceCheck="false"
				/>
			</div>
			<div class="event-milestones__total-right">
				<Tooltip :placement="Placements.LEFT" customWidth="15rem">
					<template #element>
						<AppIconButton
							class="event-milestones__total-info"
							:type="IconTypes.INFO"
							:size="32"
							:iconSize="24"
						/>
					</template>
					<template #tooltip>
						<span
							class="event-milestones__total-tooltip"
							v-html="infoText"
						/>
					</template>
				</Tooltip>
			</div>
		</div>
		<div class="event-milestones__progress" v-if="attributes.type">
			<div class="event-milestones__progress--text">
				{{
					`${formatNumber(eventiumAccumulated)} / ${formatNumber(milestones?.get(getMilestoneProgress.nextMilestoneId)?.requiredSum ?? 0)}`
				}}
			</div>
			<div class="event-milestones__progress--current">
				<MilestoneBackground
					:skin="MilestoneStatus.CLAIMABLE"
					:event="attributes.type"
				/>
				<span>{{ getMilestoneProgress.lastCompletedId }}</span>
			</div>
			<div class="progress-bar">
				<div
					class="progress-bar__inner"
					:style="{
						width: getMilestoneProgress.progressPercent + '%',
					}"
				/>
			</div>
			<div class="event-milestones__progress--next">
				<MilestoneBackground
					:skin="MilestoneStatus.LOCKED"
					:event="attributes.type"
				/>
				<span>{{ getMilestoneProgress.nextMilestoneId }}</span>
			</div>
		</div>
		<HorizontalDecorDivider :withSides="true" />

		<ScrollFade>
			<template #scrollContent>
				<div v-if="milestones" class="event-milestones__content">
					<div class="event-milestones__pass">
						<div
							v-if="eventPassPurchased && attributes.type"
							class="event-milestones__pass--premium"
						>
							<PassesBackground :event="attributes.type" />
							<div
								class="event-milestones__pass--premium-content"
							>
								<div
									class="event-milestones__pass--premium-content-text"
								>
									<ShrinkText
										:text="
											translate('event.free_milestones')
										"
										:fontSize="18"
										containerClassList="threshold-container"
										textClassList="threshold"
										:maxLineLimit="2"
									/>
								</div>
								<AppMainIcon
									class="event-milestones__pass--premium-icon"
									:iconName="ResourceType.EVENTIUM"
									:iconSize="44"
								/>
								<div
									class="event-milestones__pass--premium-content-text"
								>
									<ShrinkText
										:text="
											translate(
												`offer.lok_${attributes.type}_pass.non_translatable`,
											)
										"
										:fontSize="18"
										containerClassList="threshold-container"
										textClassList="threshold"
										:maxLineLimit="2"
									/>
								</div>
							</div>
							<div class="done-icon">
								<div class="icon-check" />
							</div>
						</div>
						<div v-else class="event-milestones__pass--no-pass">
							<div
								class="event-milestones__pass--free"
								v-if="attributes.type"
							>
								<FreePassBackground :event="attributes.type" />
								<div
									class="event-milestones__pass--free-content"
								>
									<div
										class="event-milestones__pass--free-text"
									>
										<ShrinkText
											:text="
												translate(
													'event.free_milestones',
												)
											"
											:fontSize="18"
											containerClassList="threshold-container"
											textClassList="threshold"
											:maxLineLimit="2"
										/>
									</div>
									<AppMainIcon
										class="event-milestones__pass--free-icon"
										:iconName="ResourceType.EVENTIUM"
										:iconSize="44"
									/>
								</div>
							</div>
							<AppTextButton
								class="pass-btn"
								:text="
									translate(
										`offer.lok_${attributes.type}_pass.non_translatable`,
									)
								"
								:secondText="translate('event.activate')"
								:secondFontSize="11"
								:type="StatusTypes.SECONDARY"
								notification="!"
								@click="goToShop"
							/>
						</div>
					</div>

					<div class="event-milestones__items">
						<div
							v-for="[id, milestone] in milestones"
							:key="id"
							class="event-milestones__item"
							:class="`status-${milestone.status}-${milestone.premium?.status}`"
						>
							<div
								class="event-milestones__item--free"
								:class="{
									disabled:
										milestone.status ===
										MilestoneStatus.CLAIMED,
								}"
								@click="
									handleMilestoneProgressStepClick(
										id,
										milestone,
										false,
									)
								"
							>
								<EventMilestonesReward
									:reward="milestone.reward"
								/>
								<div
									v-if="
										milestone.status ===
										MilestoneStatus.LOCKED
									"
									class="icon-lock"
								/>
								<div
									class="done-icon"
									v-if="
										milestone.status ===
										MilestoneStatus.CLAIMED
									"
								>
									<div class="icon-check" />
								</div>
								<AppNotification
									v-if="
										milestone.status ===
										MilestoneStatus.CLAIMABLE
									"
									class="notification"
									value="!"
									:type="StatusTypes.WARNING"
								/>
							</div>
							<div
								class="event-milestones__item--premium"
								:class="{
									disabled:
										milestone.premium?.status ===
										MilestoneStatus.CLAIMED,
								}"
								@click="
									handleMilestoneProgressStepClick(
										id,
										milestone,
										true,
									)
								"
							>
								<EventMilestonesReward
									v-if="milestone.premium"
									:reward="milestone.premium?.reward"
								/>
								<div
									v-if="
										milestone.premium?.status ===
											MilestoneStatus.LOCKED ||
										!eventPassPurchased
									"
									class="icon-lock"
								/>
								<div
									class="done-icon"
									v-if="
										milestone.premium?.status ===
										MilestoneStatus.CLAIMED
									"
								>
									<div class="icon-check" />
								</div>
								<AppNotification
									v-if="
										milestone.premium?.status ===
										MilestoneStatus.CLAIMABLE
									"
									class="notification"
									value="!"
									:type="StatusTypes.WARNING"
								/>
							</div>
							<div
								class="event-milestones__pass-connection"
								:class="{
									light:
										id >
										getMilestoneProgress.nextMilestoneId,
								}"
							/>
							<div class="event-milestones__item-counter">
								<Tooltip
									customMaxWidth="12rem"
									:heading="
										translate(
											'event.milestone_num',
										).replace('{number}', id.toString())
									"
									:placement="Placements.TOP"
								>
									<template #element>
										<div
											class="event-milestones__item-counter--content"
										>
											<div class="arrow arrow--left-22" />
											<span>{{ id }}</span>
											<div
												class="arrow arrow--right-22"
											/>
										</div>
										<MilestoneBackground
											v-if="attributes.type"
											:event="attributes.type"
											:skin="getMilestoneIdBackground(id)"
										/>
									</template>
									<template #tooltip>
										<div
											class="event-milestones__item-counter--tooltip"
										>
											<div>
												{{
													`${translate(
														"event.required_amount",
													)}: `
												}}
											</div>
											<div
												class="event-milestones__item-counter--tooltip-value"
											>
												<span>
													{{
														formatNumber(
															milestone.requiredSum,
														)
													}}
												</span>
												<AppMainIcon
													:iconName="
														ResourceType.EVENTIUM
													"
													:iconSize="14"
												/>
											</div>
										</div>
									</template>
								</Tooltip>
							</div>

							<ToastMessage
								v-if="showingToast.has(id)"
								:showMessage="true"
								:message="'claimed'"
								:hasAnimation="true"
								class="milestone-toast"
							/>
						</div>
					</div>
				</div>
			</template>
		</ScrollFade>
	</div>
</template>

<script lang="ts" setup>
import AppPrice from "@/components/AppPrice.vue";
import ScrollFade from "@/components/ScrollFade.vue";
import ShrinkText from "@/components/ShrinkText.vue";
import ToastMessage from "@/components/ToastMessage.vue";
import Tooltip from "@/components/Tooltip.vue";
import useRewardClaimGuard from "@/hooks/useRewardClaimGuard";
import type {
	MilestoneInGameRewardBuilding,
	MilestoneItem,
} from "@/types/event";
import { MilestoneStatus } from "@/types/event";

import AppIconButton from "@/components/AppIconButton.vue";
import AppMainIcon from "@/components/AppMainIcon.vue";
import AppNotification from "@/components/AppNotification.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import FreePassBackground from "@/components/SVG/Event/FreePassBackground.vue";
import MilestoneBackground from "@/components/SVG/Event/MilestoneBackground.vue";
import PassesBackground from "@/components/SVG/Event/PassesBackground.vue";
import HorizontalDecorDivider from "@/components/SVG/HorizontalDecorDivider.vue";
import {
	applyMilestoneBuilding,
	applyMilestoneCurrency,
	saveMilestoneClaim,
	useEventStore,
} from "@/event";
import { isPastEndDate } from "@/event/session/eventEndDate";
import { ResourceType } from "@/game/entities/config/resources";
import { TileToastType, ToastPlacement } from "@/game/ToastMessage";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useSocketService from "@/hooks/useSocketService";
import {
	OfferAnalyticsClickSource,
	OfferAnalyticsType,
} from "@/libs/SocketService.ts";
import { PaymentProvider } from "@/services/PaymentProvider.ts";
import useResourcesStore from "@/store/useResourcesStore";
import useShopStore from "@/store/useShopStore";
import { IconTypes, Placements, StatusTypes } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import EventMilestonesReward from "./EventMilestonesReward.vue";

const claimGuard = useRewardClaimGuard();

const resourcesStore = useResourcesStore();
const { eventiumAccumulated } = storeToRefs(resourcesStore);
const eventStore = useEventStore();
const { milestones, attributes, eventPassPurchased } = storeToRefs(eventStore);
const isEventEnded = computed((): boolean => isPastEndDate(eventStore.endDate));

const shopStore = useShopStore();

const showingToast = ref<Set<number>>(new Set());
const isClaiming = ref<boolean>(false);

const infoText = computed((): string => {
	return translate("event.milestone_info").replace(
		"{resource}",
		`<span class='text-icon icon-${eventStore.attributes.type}-eventium-16'></span>`,
	);
});

const getMilestoneProgress = computed(
	(): {
		lastCompletedId: number;
		nextMilestoneId: number;
		progressPercent: number;
	} => {
		let lastCompletedId = 0;
		let nextMilestoneId = 0;
		let progressPercent = 0;

		if (!milestones.value) {
			return {
				lastCompletedId,
				nextMilestoneId,
				progressPercent,
			};
		}

		const entries = Array.from(milestones.value.entries());

		const nextIndex = entries.findIndex(
			([, data]: [id: number, data: MilestoneItem]): boolean =>
				eventiumAccumulated.value < data.requiredSum,
		);

		if (nextIndex === -1) {
			lastCompletedId = entries[entries.length - 1][0];
			nextMilestoneId = lastCompletedId;
			progressPercent = 100;
		} else {
			nextMilestoneId = entries[nextIndex][0];

			if (nextIndex > 0) {
				lastCompletedId = entries[nextIndex - 1][0];
			}

			const nextSum = entries[nextIndex][1].requiredSum;

			const prevSum =
				nextIndex > 0 ? entries[nextIndex - 1][1].requiredSum : 0;

			const range = nextSum - prevSum;
			const currentInRange = eventiumAccumulated.value - prevSum;

			progressPercent = (currentInRange / range) * 100;
		}

		return {
			lastCompletedId,
			nextMilestoneId,
			progressPercent: parseFloat(progressPercent.toFixed(2)),
		};
	},
);

const getMilestoneIdBackground = (id: number): MilestoneStatus => {
	const milestone = milestones.value?.get(id);
	if (id === getMilestoneProgress.value.lastCompletedId) {
		return MilestoneStatus.CLAIMABLE;
	}
	return milestone?.status ?? MilestoneStatus.LOCKED;
};

const handleMilestoneProgressStepClick = (
	id: number,
	milestone: MilestoneItem | null,
	isPremium?: boolean,
): Promise<void> =>
	claimGuard.guard(async (): Promise<void> => {
		if (isEventEnded.value) return;
		if (
			isPremium &&
			milestone?.premium?.status === MilestoneStatus.CLAIMABLE &&
			!eventPassPurchased.value
		) {
			goToShop();
			return;
		}
		if (isClaiming.value) return;

		isClaiming.value = true;

		try {
			if (
				(!isPremium &&
					milestone?.status === MilestoneStatus.CLAIMABLE) ||
				(isPremium &&
					eventPassPurchased.value &&
					milestone?.premium?.status === MilestoneStatus.CLAIMABLE)
			) {
				const milestoneReward =
					isPremium && milestone.premium
						? milestone.premium.reward
						: milestone.reward;

				if (milestoneReward.type === "currency") {
					if (
						!applyMilestoneCurrency(
							milestoneReward,
							"event milestone reward",
						)
					) {
						return;
					}
				}

				if (milestoneReward.type === "item") {
					const reward =
						milestoneReward as MilestoneInGameRewardBuilding;
					if (
						!applyMilestoneBuilding(
							reward,
							"event milestone reward",
						)
					) {
						return;
					}
				}

				const isFreeClaimed =
					milestone.status === MilestoneStatus.CLAIMED;
				const isPremiumClaimed =
					milestone.premium?.status === MilestoneStatus.CLAIMED;

				const freeStatus = isPremium ? isFreeClaimed : true;
				const premiumStatus = !isPremium ? isPremiumClaimed : true;

				const patch = {
					[id]: { free: freeStatus, premium: premiumStatus },
				};
				eventStore.mergeClaimedMilestonesOptimistic(patch);

				showingToast.value.add(id);
				setTimeout((): void => {
					showingToast.value.delete(id);
				}, 1000);

				const ok = await saveMilestoneClaim({
					eventMilestones: patch,
				});
				if (!ok) {
					useEmitter().emit(EmitterEvents.PhaserTextMessage, {
						text: translate("ui.connection_error_info"),
						type: TileToastType.WARNING,
						placement: ToastPlacement.TOP_CENTER,
					});
				}
			}
		} finally {
			isClaiming.value = false;
		}
	});

const goToShop = (): void => {
	eventStore.setEventPopupActive(false);
	const storeId = attributes.value.event_pass_store_id;

	if (!storeId) return;

	useSocketService().emitOfferAnalyticsSave({
		type: OfferAnalyticsType.OFFER_CLICK,
		clickSource: OfferAnalyticsClickSource.POPUP ?? "UNKNOWN",
		storeId: storeId ?? "UNKNOWN",
		offerName: shopStore.getItemByStoreId(storeId)?.name ?? "UNKNOWN",
	});

	PaymentProvider.openPopup(storeId, {
		clickSource: OfferAnalyticsClickSource.POPUP,
	});
};
</script>

<style lang="scss" scoped>
.event-milestones {
	$connectorHeight: 1rem;
	align-items: center;
	padding: 0.5rem 0rem;
	&__info {
		margin-top: 1rem;
		padding: 0 2.375rem;
		color: #fff3dd;
		font-size: 0.875rem;
		line-height: 1.25rem;
		text-align: center;
	}

	&__total {
		@include flex-row-between;
		margin-bottom: 0.5rem;
		padding: 0 0.75rem;
		width: 100%;
		&-tooltip {
			text-align: center;
		}
		&-left {
			display: flex;
			position: relative;
			flex: 1 1 0;
			align-items: center;
			color: #f4d278;
			font-size: 0.906rem;
			:deep(.price span) {
				color: #f4d278;
			}
		}

		&-left {
			width: 80%;
		}
	}
	&__progress {
		display: flex;
		@include flex-row-center;
		position: relative;
		margin-top: 0.5rem;
		padding: 0 0.75rem;
		width: 100%;
		&--current,
		&--next {
			position: relative;
			width: 10rem;
			height: 1.875rem;
			& > span {
				@include flex-row-center;
				position: absolute;
				top: 0;
				left: 0;
				padding-bottom: 0.25rem;
				width: 100%;
				height: 100%;
				color: #fff3dd;
				font-weight: bold;
				font-size: 1.188rem;
				text-shadow: 0.044rem 0.044rem 0 rgba(0, 0, 0, 0.6);
			}
		}
		&--current {
			left: 0.3rem;
		}
		&--next {
			right: 0.3rem;
		}
		&--text {
			position: absolute;
			color: #f2dcb5;
			font-weight: bold;
			@include font-size(14.5);
			top: -1rem;
		}
		.progress-bar {
			box-shadow: 0 0.063rem 0.063rem 0 rgba(35, 18, 4, 0.6);
			background-color: #0b0b0b;
			width: 100%;
			height: 0.375rem;

			&__inner {
				position: relative;
				background-color: #ffcc32;
				height: 100%;
				&::before {
					position: absolute;
					top: 0;
					left: 0;
					opacity: 1 !important;
					// NOTE: Multiply blending is better for light colors
					mix-blend-mode: multiply !important;
					background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
						repeat center;
					background-size: 40%;
					width: 100%;
					height: 100%;
					content: "";
				}
			}
		}
	}
	:deep(.horizontal-decor-divider) {
		margin: 0.813rem 0;
		padding: 0 0.75rem;
		.cls-1 {
			fill: #928b18;
		}
	}

	&__content {
		padding-top: 0.2rem;
		padding-right: 0.4rem;
		width: 100%;
		.pass-btn {
			padding: 0.1rem;
			max-width: 9.6rem;
		}
	}
	&__pass {
		position: relative;
		z-index: 1;
		margin: 0 auto;
		margin-bottom: 1rem;
		padding-right: 0.1rem;
		width: 95%;
		&-connection {
			position: absolute;
			top: -1.5rem;
			left: 50%;
			transform: translateX(-50%);
			border-width: 0.075rem;
			border-style: solid;
			border-image-source: linear-gradient(to top, #bd9032, #f0d386);
			border-image-slice: 1;
			background-color: #ffb41f;
			width: 0.5rem;
			height: 1.813rem;
			&.light {
				border-image-source: linear-gradient(to top, #bd9032, #f0d386);
				background-color: #f2dcb5;
			}
			&::before {
				position: absolute;
				top: 0;
				left: 0;
				opacity: 1 !important;
				// NOTE: Multiply blending is better for light colors
				mix-blend-mode: multiply !important;
				background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
					repeat center;
				background-size: 40%;
				width: 100%;
				height: 100%;
				content: "";
			}
		}
		&--no-pass {
			@include flex-row-center;
			gap: 0.625rem;
			width: 100%;
		}
		&--free {
			position: relative;
			flex-shrink: 0;
			width: 57.5%;
			height: 4rem;
			&-icon {
				position: relative;
				right: 0.3rem;
			}
			&-content {
				display: flex;
				position: absolute;
				top: 0;
				left: 0;
				justify-content: space-between;
				align-items: center;
				width: 100%;
				height: 100%;
			}
			&-text {
				margin-bottom: 0.25rem;
				padding-left: 1rem;
				width: 80%;
				color: #fff3dd;
				font-weight: bold;
				text-align: center;
				text-transform: uppercase;
			}
		}
		&--premium {
			position: relative;
			z-index: 1;
			&-content {
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				@include flex-row-center;
				&-text {
					margin-bottom: 0.25rem;
					width: 40%;
					color: #fff3dd;
					font-weight: bold;
					text-transform: uppercase;
				}
			}
		}
		.done-icon {
			position: absolute;
			top: 0.1rem;
			right: -0.35rem;
		}
	}

	&__items {
		@include flex-col-center;
		gap: $connectorHeight;
		padding: 0 0.75rem;
	}

	&__item {
		position: relative;
		box-shadow: inset 0 0 0.5rem 0 #20150b;
		border-width: 0.075rem;
		border-style: solid;
		border-image-source: linear-gradient(to top, #bd9032, #f0d386);
		border-image-slice: 1;
		border-image-slice: 1;
		width: 100%;
		height: 3rem;
		@include flex-row-center;
		&--free,
		&--premium {
			position: relative;
			width: 50%;
			height: 100%;
			@include flex-row-center;
			&.disabled {
				pointer-events: none;
				:deep(.event-milestones-reward) {
					opacity: 0.6;
				}
			}
			.done-icon,
			.notification {
				position: absolute;
				top: -0.5rem;
			}
			.icon-lock {
				position: absolute;
				width: 1.375rem;
				height: 1.375rem;
			}
		}
		&--free {
			padding-right: 3.4rem;
			padding-left: 1.75rem;
			.done-icon,
			.notification {
				left: -0.5rem;
			}
			.icon-lock {
				left: 0.375rem;
			}
		}
		&--premium {
			padding-right: 1.75rem;
			padding-left: 3.4rem;
			.done-icon,
			.notification {
				right: -0.5rem;
			}
			.icon-lock {
				right: 0.313rem;
			}
		}

		&.status {
			&-claimed-claimed {
				background: linear-gradient(
					90deg,
					rgba(142, 175, 31, 1) 0%,
					rgba(60, 75, 7, 1) 50%,
					rgba(142, 175, 31, 1) 100%
				);
			}
			&-claimed-claimable {
				background: linear-gradient(
					90deg,
					rgba(142, 175, 31, 1) 0%,
					rgba(60, 75, 7, 1) 50%,
					rgba(255, 180, 31, 1) 100%
				);
			}
			&-claimed-locked {
				background: linear-gradient(
					90deg,
					rgba(142, 175, 31, 1) 0%,
					rgba(60, 75, 7, 1) 50%,
					rgba(111, 58, 139, 1) 100%
				);
			}
			&-claimable-claimed {
				background: linear-gradient(
					90deg,
					rgba(255, 180, 31, 1) 0%,
					rgba(60, 75, 7, 1) 50%,
					rgba(142, 175, 31, 1) 100%
				);
			}
			&-claimable-claimable {
				background: linear-gradient(
					90deg,
					rgba(255, 180, 31, 1) 0%,
					rgba(60, 75, 7, 1) 50%,
					rgba(255, 180, 31, 1) 100%
				);
			}
			&-claimable-locked {
				background: linear-gradient(
					90deg,
					rgba(255, 180, 31, 1) 0%,
					rgba(60, 75, 7, 1) 50%,
					rgba(111, 58, 139, 1) 100%
				);
			}
			&-locked-locked {
				background: linear-gradient(
					90deg,
					rgba(146, 139, 24, 1) 0%,
					rgba(45, 45, 45, 1) 50%,
					rgba(111, 58, 139, 1) 100%
				);
			}
		}

		&-counter {
			position: absolute;
			width: 6.813rem;
			height: 2.375rem;
			&--content {
				display: flex;
				justify-content: space-evenly;
				align-items: center;
				z-index: 1;
				color: #fff3dd;
				@include font-size(19);
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				font-weight: bold;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
				& > span {
					margin-bottom: 0.25rem;
				}
			}
			&--tooltip {
				color: #372615;
				@include font-size(13);
				@include flex-col-center;
				&-value {
					display: flex;
					gap: 0.15rem;
					& > span {
						font-weight: bold;
						@include font-size(14.5);
					}
				}
			}
			.arrow {
				bottom: 0.05rem;
			}
			.arrow--left-22 {
				left: -0.35rem;
			}
			.arrow--right-22 {
				right: -0.45rem;
			}
		}

		&::before {
			position: absolute;
			top: 0;
			left: 0;
			opacity: 1 !important;
			// NOTE: Multiply blending is better for light colors
			mix-blend-mode: multiply !important;
			background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
				repeat center;
			background-size: 40%;
			width: 100%;
			height: 100%;
			content: "";
		}

		&--claimed {
			background-image: linear-gradient(
				90deg,
				rgba(25, 179, 69, 1) 40%,
				rgba(13, 108, 39, 1) 100%
			);

			:deep(.price__value),
			:deep(.amount__value) {
				color: #fff3dd;
			}
		}

		&--claimable {
			background-image: linear-gradient(
				90deg,
				rgba(244, 210, 120, 1) 40%,
				rgba(217, 135, 28, 1) 100%
			);
		}

		&--claimable,
		&--locked {
			:deep(.price__value),
			:deep(.amount__value) {
				color: #372615;
				text-shadow: none;
			}
		}

		:deep(.milestone-toast) {
			position: absolute;
			top: -0.5rem;
			left: 40%;
			transform: translateX(-50%);
			z-index: 10;
		}
	}

	.done-icon {
		border: 0.075rem solid transparent;

		border-radius: 50%; /* Creates a circular shape */

		background-image:
			linear-gradient(to top, #181a1d, #303239),
			linear-gradient(to top, #bd9032, #f0d386);

		background-origin: border-box;
		background-clip: padding-box, border-box;

		width: 1rem;
		height: 1rem;
		@include flex-row-center;
		:deep(.icon-check) {
			position: relative;
			bottom: 0.1rem;
			left: 0.1rem;
			flex-shrink: 0;
			width: 1rem;
			height: 1rem;
		}
	}

	&[data-event-type="fishing"] {
		.event-milestones__item {
			&.status-locked-locked {
				background: linear-gradient(
					90deg,
					#0f87b3 0%,
					rgba(45, 45, 45, 1) 50%,
					rgba(111, 58, 139, 1) 100%
				);
			}
		}
	}
	&[data-event-type="firefest"] {
		.event-milestones__item {
			&.status-locked-locked {
				background: linear-gradient(
					90deg,
					rgba(203, 58, 19, 1) 0%,
					rgba(53, 23, 15, 1) 50%,
					rgba(111, 58, 139, 1) 100%
				);
			}
		}
	}
	&[data-event-type="hunting"] {
		.event-milestones__item {
			&.status-locked-locked {
				background: linear-gradient(
					90deg,
					rgba(17, 75, 12, 1) 0%,
					rgba(8, 36, 3, 1) 50%,
					rgba(111, 58, 139, 1) 100%
				);
			}
		}
	}
	&[data-event-type="halloween"] {
		.event-milestones__item {
			&.status-claimed-claimed {
				background: linear-gradient(
					90deg,
					rgba(15, 152, 54, 1) 0%,
					rgba(41, 32, 61, 1) 50%,
					rgba(15, 152, 54, 1) 100%
				);
			}
			&.status-claimed-claimable {
				background: linear-gradient(
					90deg,
					rgba(15, 152, 54, 1) 0%,
					rgba(41, 32, 61, 1) 50%,
					rgba(227, 114, 38, 1) 100%
				);
			}
			&.status-claimed-locked {
				background: linear-gradient(
					90deg,
					rgba(15, 152, 54, 1) 0%,
					rgba(41, 32, 61, 1) 50%,
					rgba(137, 55, 181, 1) 100%
				);
			}
			&.status-claimable-claimed {
				background: linear-gradient(
					90deg,
					rgba(227, 114, 38, 1) 0%,
					rgba(41, 32, 61, 1) 50%,
					rgba(15, 152, 54, 1) 100%
				);
			}
			&.status-claimable-claimable {
				background: linear-gradient(
					90deg,
					rgba(227, 114, 38, 1) 0%,
					rgba(41, 32, 61, 1) 50%,
					rgba(227, 114, 38, 1) 100%
				);
			}
			&.status-claimable-locked {
				background: linear-gradient(
					90deg,
					rgba(227, 114, 38, 1) 0%,
					rgba(41, 32, 61, 1) 50%,
					rgba(137, 55, 181, 1) 100%
				);
			}
			&.status-locked-locked {
				background: linear-gradient(
					90deg,
					rgba(62, 35, 126, 1) 0%,
					rgba(41, 32, 61, 1) 50%,
					rgba(137, 55, 181, 1) 100%
				);
			}
		}
	}
}
</style>
